import {createSlice} from '@reduxjs/toolkit';
import _cloneDeep from 'lodash/cloneDeep';
import _find from 'lodash/find';
import _forEach from 'lodash/forEach';
import _isEmpty from 'lodash/isEmpty';
import API from '../../app/api';
import {
  getBulkObjectChange,
  prepareAdmitConsultantChanges,
  getConsultantWithDefaultAdmittingSpecialty,
} from '../../pages/centralPage/helper';
import {setSnackbar} from './globals';
import {hasPermission, Permissions, sendUserAudit} from './user';
import { AUDIT_TYPES } from '../../util/auditUtil';

export const CENTRAL_PAGE_CONTEXT_OPTIONS = {
  ABOUT: 'about',
  LOGOUT: 'logout',
  SUPPORT: 'support',
  MODULES: 'modules',
  PREFERENCES: 'preferences',
};

const ADMITTING_CONSULTANT_MODAL_STATE = {
  changesObj: {}, // specId: consultant,
  activeSpecialties: [],
  activeConsultants: [],
  defaultConsultants: {},
}

export const centralPage = createSlice({
  name: 'centralPage',
  initialState: {
    admittingConsultant: ADMITTING_CONSULTANT_MODAL_STATE,
    centralPageContext: CENTRAL_PAGE_CONTEXT_OPTIONS.MODULES,
  },
  reducers: {
    setCentralPageContext: (state, context) => {
      state.centralPageContext = context.payload;
    },
    setAdmittingConsultantState: (state, context) => {
      state.admittingConsultant = {
        ...state.admittingConsultant,
        ...context.payload,
      }
    },
  }
});

// Setters
export const {
  setCentralPageContext,
  setAdmittingConsultantState,
} = centralPage.actions;

// Variable
export const centralPageContext = (state) => state.centralPage.centralPageContext;
export const activeSpecialties = (state) => state.centralPage.admittingConsultant.activeSpecialties;
export const activeConsultants = (state) => state.centralPage.admittingConsultant.activeConsultants;
export const defaultConsultants = (state) => state.centralPage.admittingConsultant.defaultConsultants;
export const groupedConsultants = (state) => state.centralPage.admittingConsultant.groupedConsultants;
export const consultantAdmittingSpecChangesObj = (state) => state.centralPage.admittingConsultant.changesObj;

// Handler functions
export const cleanCentralPageState = () => (dispatch) => {
  dispatch(setAdmittingConsultantState(ADMITTING_CONSULTANT_MODAL_STATE));
}

export const canManageAdmittingConsultants = () => (dispatch) => {
  return dispatch(hasPermission(Permissions.AGYLE_CAN_MANAGE_ADMITTING_CONSULTANTS));
}

export const tryAddToChangesList = (consultant, specialty) => (dispatch, getState) => {
  const specialtyKey = specialty.__id;
  const changesClone = _cloneDeep(getState().centralPage.admittingConsultant.changesObj);

  changesClone[specialtyKey] = consultant;

  dispatch(setAdmittingConsultantState({
    changesObj: changesClone,
  }))

  return changesClone;
}

export const getChangesValue = (specialtyId) => (dispatch, getState) => {
  const changesClone = _cloneDeep(getState().centralPage.admittingConsultant.changesObj);
  const currentChangedValue = changesClone?.[specialtyId];

  if (currentChangedValue) {
    return changesClone?.[specialtyId]?.__id;
  }

  return currentChangedValue;
}

export const bulkSendAdmittingConsultantChanges = ({ ward, onClose }) => async (dispatch, getState) => {
  const promises = [];
  const currentWard = ward || getState().ward.value;
  const defaultConsultantsObject = getState().centralPage.admittingConsultant.defaultConsultants;
  const changesClone = _cloneDeep(getState().centralPage.admittingConsultant.changesObj);
  const groupedConsultantsObject = getState().centralPage.admittingConsultant.groupedConsultants;

  if (!_isEmpty(Object.keys(changesClone))) {
    _forEach(Object.keys(changesClone), (key) => {
      const keys = Object.keys(groupedConsultantsObject);
      const foundKey = _find(keys, (localKey) => localKey.includes(key));

      const [specialty, specialtyAssignedConsultants] = groupedConsultantsObject[foundKey];
      const previousConsultant =  getConsultantWithDefaultAdmittingSpecialty(defaultConsultantsObject?.[specialty?.__id], specialtyAssignedConsultants);
      const changesObject = getBulkObjectChange({ specialty, previousConsultant, consultant: changesClone?.[key] }, currentWard);

      promises.push(dispatch(sendAdmittingConsultantChanges(changesObject)));
    })
  }

  try {
    const response = await Promise.all(promises);

    if (response && onClose) {
      onClose()
    }
  } catch (e) {
    dispatch(setSnackbar({
      type: 'error',
      message: `Error changing default admitting consultant. Please try again later.`
    }))
  }
}

export const sendAdmittingConsultantChanges = ({ currentWard, consultant, specialty, previousConsultant }) => async (dispatch) => {
  if (!currentWard || !consultant || !specialty) {
    dispatch(setSnackbar({
      type: 'error',
      message: `Error setting default admitting consultant. Please contact support.`
    }));
    return;
  }

  const abortController = new AbortController();
  const { dataObj, auditData } = prepareAdmitConsultantChanges({ currentWard, consultant, specialty, previousConsultant });

  try {
    const response = await API.updateDefaultAdmissionConsultants(abortController.signal, dataObj);

    if (response) {
      dispatch(setSnackbar({
        type: 'success',
        message: `Default admission consultant for specialty ${specialty?.__description} successfully changed.`
      }))

      dispatch(sendUserAudit(null, AUDIT_TYPES.DEFAULT_ADMITTING_CONSULTANT, auditData));
      return true;
    } else {
      return false;
    }
  } catch (e) {
    dispatch(setSnackbar({
      type: 'error',
      message: `Error changing default admitting consultant. Please try again later.`
    }))
  }
}

export default centralPage.reducer;
